import { useEffect, useState } from 'react';
import type ApolloClient from 'apollo-client';
import {
	getStaticMacroContent,
	type CustomContentResponse,
} from '../../utils/getStaticMacroContent';

const formatMapping = (
	staticMacroContent: CustomContentResponse['data']['content']['nodes'][0]['children']['nodes'],
) => {
	return staticMacroContent.reduce((acc, node) => {
		return {
			...acc,
			[node.title]: JSON.parse(node.body.atlas_doc_format.value),
		};
	}, {});
};

// Single promise at module scope so all hook instances share the same in-flight request.
// When it resolves, every hooks setState runs, so each component re-renders with the result.
let adfMappingPromise: Promise<Record<string, any> | null> | null = null;

// Fetches the static macro content and formats the mapping. The promise is shared so
// we only fetch once; all hook instances subscribe to the same promise and re-render when it resolves.
export const useGetStaticMacroContent = ({
	apolloClient,
	pageId,
	customContentType,
}: {
	apolloClient: ApolloClient<object>;
	customContentType: string;
	pageId: string;
}) => {
	const [adfMapping, setAdfMapping] = useState<Record<string, any> | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!adfMappingPromise) {
			adfMappingPromise = getStaticMacroContent({
				apolloClient,
				pageId,
				type: customContentType,
			})
				.then(formatMapping)
				.catch(() => null);
		}

		// Cancel the promise if the hook is unmounted
		let cancelled = false;

		adfMappingPromise
			.then((mapping) => {
				if (!cancelled) {
					setAdfMapping(mapping);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [apolloClient, pageId, customContentType]);

	return { adfMapping, isLoading };
};
